import React, { useState, useEffect } from 'react';
import { db } from '../Firebase';
import { collection, onSnapshot, query, orderBy, updateDoc, doc, where, deleteDoc } from 'firebase/firestore';
import { 
  Search, 
  Filter, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  ArrowRight,
  User,
  Phone,
  Briefcase,
  MapPin,
  Mail,
  Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';

const LoanInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['All', 'Gold Loan', 'Mortgage Loan', 'Loan Against Deposit', 'Business Loan', 'Agriculture Allied Loan', 'Vehicle Loan'];

  useEffect(() => {
    const q = query(collection(db, 'loan_inquiries'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setInquiries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const handleFollowUp = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'loan_inquiries', id), {
        followUpDone: !currentStatus,
        updatedAt: new Date()
      });
      Swal.fire({
        icon: 'success',
        title: 'Status Updated',
        text: `Marked as ${!currentStatus ? 'Follow-up Done' : 'Remaining'}`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'loan_inquiries', id));
        Swal.fire(
          'Deleted!',
          'Inquiry has been deleted.',
          'success'
        );
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Failed to delete inquiry', 'error');
      }
    }
  };

  const filteredInquiries = inquiries.filter(inq => {
    const matchesCategory = activeCategory === 'All' || inq.category === activeCategory;
    const date = inq.createdAt?.toDate();
    const matchesMonth = !filterMonth || (date && (date.getMonth() + 1).toString() === filterMonth);
    const matchesYear = !filterYear || (date && date.getFullYear().toString() === filterYear);
    const matchesSearch = !searchTerm || inq.name?.toLowerCase().includes(searchTerm.toLowerCase()) || inq.phone?.includes(searchTerm);
    
    return matchesCategory && matchesMonth && matchesYear && matchesSearch;
  });

  return (
    <div className="space-y-8">
      {/* Header & Main Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-primary-dark">Loan Inquiries</h1>
          <p className="text-gray-500 font-medium">Review and track new loan requests from prospective members.</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-4">
          <div className="bg-white px-4 py-2 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <select 
                value={filterMonth} 
                onChange={(e) => setFilterMonth(e.target.value)}
                className="bg-transparent border-none outline-none text-sm font-bold text-gray-700"
              >
                <option value="">All Months</option>
                {Array.from({length: 12}, (_, i) => (
                  <option key={i+1} value={(i+1).toString()}>
                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-px h-6 bg-gray-100" />
            <select 
              value={filterYear} 
              onChange={(e) => setFilterYear(e.target.value)}
              className="bg-transparent border-none outline-none text-sm font-bold text-gray-700"
            >
              {Array.from({ length: new Date().getFullYear() - 2026 + 1 }, (_, i) => 2026 + i).map(y => (
                <option key={y} value={y.toString()}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3 bg-white px-4 py-2.5 rounded-2xl border border-gray-100 shadow-sm focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <Search className="w-5 h-5 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search by name or phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent border-none outline-none text-sm text-gray-700 w-48"
            />
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-6 py-3 rounded-2xl text-sm font-bold transition-all ${
              activeCategory === cat 
              ? 'bg-primary text-white shadow-lg shadow-primary/20' 
              : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-100'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Inquiries List */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AnimatePresence>
          {filteredInquiries.length > 0 ? (
            filteredInquiries.map((inq, index) => (
              <motion.div
                key={inq.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all group"
              >
                <div className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 group-hover:bg-primary group-hover:text-white transition-colors">
                        <User className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-primary-dark">{inq.name}</h3>
                        <span className="text-xs font-bold text-secondary uppercase tracking-widest">{inq.category}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleFollowUp(inq.id, inq.followUpDone)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                        inq.followUpDone 
                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                        : 'bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100'
                      }`}
                    >
                      {inq.followUpDone ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      {inq.followUpDone ? 'Done' : 'Pending'}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="p-2 bg-gray-50 rounded-lg"><Mail className="w-4 h-4" /></div>
                      <span className="text-sm font-medium truncate">{inq.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="p-2 bg-gray-50 rounded-lg"><Phone className="w-4 h-4" /></div>
                      <span className="text-sm font-medium">{inq.phone}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="p-2 bg-gray-50 rounded-lg"><MapPin className="w-4 h-4" /></div>
                      <span className="text-sm font-medium">{inq.city || 'Location not specified'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <div className="p-2 bg-gray-50 rounded-lg"><Briefcase className="w-4 h-4" /></div>
                      <span className="text-sm font-medium">₹ {inq.amount}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-gray-50">
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                      <Calendar className="w-4 h-4" />
                      {inq.createdAt?.toDate().toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <button
                      onClick={() => handleDelete(inq.id)}
                      className="p-2 rounded-xl bg-red-50 text-red-600 border border-red-100 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm transition-all"
                      title="Delete Inquiry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full py-20 text-center bg-white rounded-[3rem] border border-dashed border-gray-200">
              <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-bold text-primary-dark">No inquiries found</h3>
              <p className="text-gray-500">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LoanInquiries;
